import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';

// Design System
import { Modal } from 'jl-design-system/components/modal/Modal';
import { PrimaryButton, SecondaryButton, TertiaryButton } from 'jl-design-system/elements/button/Button';

// Types
import { AppDispatch } from 'types/RootState.types';
import { PaymentCardDeleteProps } from './PaymentCardDelete.types';

// Config
import paymentCardDeleteState from './PaymentCardDelete.state';
import { useWindowSize } from '@context/window/WindowSizeContext';
import { goBack } from '@redux/actions/navigation/navigationActions';
import { showPaymentCardDeleteModal, deletePaymentCard } from '@redux/actions/payment/paymentActions';
import { getLast4Digits } from '@utils/helpers/cardDetails';
import cardLogos, { cardTypeText } from '@utils/helpers/cardLogos';
import { disabledEscKeyHandler } from '@utils/helpers/eventKeyActions';
import { closeAllModals } from '@redux/actions/app/appActions';

// Components
import BodyText from '@components/body-text';
import Container from '@components/container';

// Styles
import styles from './payment-card-delete.scss';

const PaymentCardDelete = ({ disabled = false }: PaymentCardDeleteProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    paymentCardDeleteApiCallActive,
    selectedPaymentCard,
    shouldShowPaymentCardDeleteModal,
    singlePageCheckoutEnabled,
  } = useSelector(paymentCardDeleteState);
  const { isDesktop } = useWindowSize();
  const modalCloseHandledRef = useRef<boolean>(false);

  if (!selectedPaymentCard) {
    return null;
  }

  useEffect(() => {
    modalCloseHandledRef.current = false;
  }, [shouldShowPaymentCardDeleteModal]);

  useEffect(() => {
    document.addEventListener('keydown', disabledEscKeyHandler, true);

    return () => {
      document.removeEventListener('keydown', disabledEscKeyHandler, true);
    };
  }, []);

  const { id, obfuscatedPan = '', type } = selectedPaymentCard;

  const onClickHandler = () => dispatch(showPaymentCardDeleteModal());
  const onCloseHandler = () => {
    if (modalCloseHandledRef.current) return;
    modalCloseHandledRef.current = true;
    if (singlePageCheckoutEnabled) {
      dispatch(closeAllModals());
    } else {
      dispatch(goBack());
    }
  };
  const last4Digits = getLast4Digits(obfuscatedPan);
  const onDeleteHandler = () => dispatch(deletePaymentCard(id, last4Digits));

  return (
    <>
      <TertiaryButton data-testid="payment-card-delete-button" disabled={disabled} onClick={onClickHandler} small>
        Delete card
      </TertiaryButton>

      {shouldShowPaymentCardDeleteModal && (
        <Modal
          className={cx(styles.modal, {
            [styles.isProcessing]: paymentCardDeleteApiCallActive,
          })}
          footer={false}
          isOpen
          onClose={onCloseHandler}
          title="Delete payment card"
        >
          <Container>
            <BodyText marginBottom="2" testId="payment-card-delete-confirm-message">
              <strong>Are you sure you want to delete this payment card?</strong>
            </BodyText>

            {type && (
              <Container flex={{ alignItems: 'center' }}>
                <img alt={type} aria-hidden="true" className={styles.logo} src={cardLogos(type)} />
                <span data-testid="payment-card-delete-card-details">
                  {cardTypeText(type)} ending in {last4Digits}
                </span>
              </Container>
            )}

            <Container
              flex={{
                alignItems: 'center',
                flexDirection: isDesktop ? 'row' : 'col-reverse',
                flexWrap: 'wrap',
                justifyContent: isDesktop ? 'end' : 'start',
              }}
              marginTop="3"
            >
              <SecondaryButton
                data-testid="payment-card-delete-cancel"
                disabled={paymentCardDeleteApiCallActive}
                inline={isDesktop}
                onClick={onCloseHandler}
              >
                No, cancel
              </SecondaryButton>
              <PrimaryButton
                data-testid="payment-card-delete-confirm"
                inline={isDesktop}
                onClick={onDeleteHandler}
                submitting={paymentCardDeleteApiCallActive}
              >
                Yes, delete
              </PrimaryButton>
            </Container>
          </Container>
        </Modal>
      )}
    </>
  );
};

export default PaymentCardDelete;
